import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { GenericOAuth2 } from '@capacitor-community/generic-oauth2';
import { AI_API_URL } from '../../constants';

// 🔄 모든 플랫폼에서 기존 백엔드 OAuth 방식 사용 (안정성 우선)
const USE_GENERIC_OAUTH = false; // ❌ Generic OAuth2 비활성화 - 기존 백엔드 방식이 더 안정적

// 구글 클라이언트 ID들 (환경별)
const GOOGLE_CLIENT_IDS = {
  web: '350719453756-4judskavfqgca2ecuchi8ssprbqgq71u.apps.googleusercontent.com',
  ios: '350719453756-4judskavfqgca2ecuchi8ssprbqgq71u.apps.googleusercontent.com', // ✅ iOS 전용 클라이언트 ID
  android: '350719453756-skb644223pmmh7ks93j99eo7u14n7ne1.apps.googleusercontent.com' // ✅ 안드로이드 전용 클라이언트 ID (google-services.json과 일치)
};

/**
 * 구글 인증 관련 로직을 담당하는 커스텀 훅
 * @param {function} setLoginDialogOpen - 로그인 다이얼로그 상태 설정 함수 (카카오/네이버와 동일)
 * @param {function} setAuthLoading - 로딩 상태 설정 함수
 * @returns {object} - 구글 인증 관련 함수들
 */
export const useGoogleAuth = (setLoginDialogOpen = null, setAuthLoading = null) => {

  /**
   * 🆕 Generic OAuth2 방식 (구글 직접 연동) - 🔧 iOS 쿠키 공유 문제 해결
   */
  const handleGenericOAuth2 = async (platform) => {
    console.log(`🆕 [${platform}] Generic OAuth2로 구글 직접 연동 시작...`);
    
    // 🔧 iOS Safari-WebView 쿠키 공유 문제 해결을 위한 설정 수정
    const oauth2Options = {
      authorizationBaseUrl: "https://accounts.google.com/o/oauth2/auth",
      accessTokenEndpoint: "https://www.googleapis.com/oauth2/v4/token",
      scope: "email profile",
      resourceUrl: "https://www.googleapis.com/userinfo/v2/me",
      logsEnabled: true,
      pkceEnabled: true, // 🔒 보안을 위한 PKCE 활성화
      web: {
        appId: GOOGLE_CLIENT_IDS.web,
        responseType: "token", // implicit flow for web
        accessTokenEndpoint: "", // clear for implicit flow
        redirectUrl: window.location.origin + "/google-callback",
        windowOptions: "height=600,left=0,top=0,width=500",
        pkceEnabled: false // 🚨 웹에서는 PKCE 비활성화 (implicit flow와 충돌 방지)
      },
      android: {
        appId: GOOGLE_CLIENT_IDS.android,
        responseType: "code", // code flow for native
        redirectUrl: "com.googleusercontent.apps.350719453756-skb644223pmmh7ks93j99eo7u14n7ne1://", // 안드로이드 전용 OAuth 스키마
        pkceEnabled: true
      },
      ios: {
        appId: GOOGLE_CLIENT_IDS.ios,
        responseType: "code", // code flow for native
        redirectUrl: "ai.tubedigest.app://", // Bundle ID 기반
        pkceEnabled: true,
        // 🔧 iOS 쿠키 공유 문제 해결: SFSafariViewController 대신 in-app browser 사용
        customHandlerClass: "DefaultiOSHandler" // 기본 핸들러 사용
      }
    };

    console.log('🔧 OAuth2 설정 (iOS 쿠키 공유 문제 해결):', oauth2Options);
    
    try {
      const response = await GenericOAuth2.authenticate(oauth2Options);
      console.log('✅ 구글 직접 OAuth 응답:', response);
      
      // 구글 인증 완료 즉시 모달 닫고 로딩 시작
      if (setLoginDialogOpen) {
        setLoginDialogOpen(false);
        console.log('🔒 구글 로그인 모달 닫기 완료');
      }
      
      if (setAuthLoading) {
        setAuthLoading(true);
        console.log('🔄 구글 로그인 로딩 시작');
      }
      
      // 구글에서 받은 사용자 정보와 토큰을 백엔드에 전달
      const backendResponse = await sendTokenToBackend(response, platform);
      
      // 모든 처리 완료 후 로딩 종료
      if (setAuthLoading) {
        setAuthLoading(false);
        console.log('🔄 구글 로그인 로딩 종료 (성공)');
      }
      
      return backendResponse;
    } catch (error) {
      console.error('🚨 Generic OAuth2 실패:', error);
      
      // 로딩 종료
      if (setAuthLoading) {
        setAuthLoading(false);
        console.log('🔄 구글 로그인 로딩 종료 (에러)');
      }
      
      // 🔧 안드로이드 구글 로그인 특정 에러 처리 (개선됨)
      if (error.message?.includes('USER_CANCELLED')) {
        throw new Error('구글 로그인이 취소되었습니다.');
      }
      
      if (error.message?.includes('GOOGLE_PLAY_SERVICES_NOT_AVAILABLE')) {
        throw new Error('구글 플레이 서비스를 사용할 수 없습니다. 구글 플레이 스토어에서 업데이트해주세요.');
      }
      
      if (error.message?.includes('NETWORK_ERROR') || error.message?.includes('NetworkError')) {
        throw new Error('네트워크 연결을 확인해주세요.');
      }
      
      if (error.message?.includes('INVALID_CLIENT') || error.message?.includes('CLIENT_ID')) {
        throw new Error('구글 로그인 설정에 문제가 있습니다. 앱을 업데이트해주세요.');
      }
      
      if (error.message?.includes('REDIRECT_URI_MISMATCH')) {
        throw new Error('구글 로그인 설정 오류입니다. 앱을 재설치해보세요.');
      }
      
      // 🆕 클라이언트 ID 불일치 오류 처리
      if (error.message?.includes('audience') || error.message?.includes('aud')) {
        throw new Error('구글 로그인 인증 설정 오류입니다. 앱을 업데이트해주세요.');
      }
      
      // 기타 에러는 일반적인 메시지로 표시
      throw new Error(`구글 로그인 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  /**
   * 🔄 구글에서 받은 토큰을 백엔드로 전달하는 함수
   */
  const sendTokenToBackend = async (googleResponse, platform) => {
    console.log('🔄 구글 토큰을 백엔드로 전달 중...', googleResponse);
    
    try {
      // 🔧 로그 분석 결과: 실제 JWT id_token을 전송해야 함
      const idToken = googleResponse.access_token_response?.id_token || googleResponse.id_token;
      
      if (!idToken) {
        throw new Error('ID Token이 응답에 없습니다. OAuth 설정을 확인해주세요.');
      }

      const backendPayload = {
        idToken: idToken, // 실제 JWT id_token 사용 (eyJ로 시작)
        platform: platform
      };

      console.log('📤 백엔드로 전송할 데이터:', {
        ...backendPayload,
        idToken: idToken.substring(0, 50) + '...' // 토큰은 일부만 로깅
      });

      const response = await fetch(`${AI_API_URL}/auth/google/token-signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`백엔드 토큰 검증 실패: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ 백엔드 토큰 검증 완료:', result);
      return result;
      
    } catch (error) {
      console.error('🚨 백엔드 토큰 전달 실패:', error);
      // 🚨 에러 발생 시 더 자세한 정보 제공
      throw new Error(`구글 로그인 백엔드 처리 실패: ${error.message}`);
    }
  };

  /**
   * 🔙 기존 방식 (백엔드 OAuth 통함) + iOS 브라우저 자동 닫기
   */
  const handleOriginalAuth = async (platform) => {
    console.log(`🔙 [${platform}] 기존 방식으로 구글 로그인 시작...`);
    
    // 🆕 구글 인증 시작 즉시 모달 닫기 (카카오/네이버와 동일한 UX)
    if (setLoginDialogOpen) {
      setLoginDialogOpen(false);
      console.log('🔒 구글 로그인 모달 닫기 완료');
    }
    
    const googleLoginUrl = `${AI_API_URL}/api/auth/google?platform=${platform}`;

    if (platform !== 'web') {
      // 네이티브 플랫폼에서는 Capacitor Browser를 사용
      console.log(`🔐 Native Google login request on ${platform}...`);
      // 로딩 시작 (모달은 LoginDialog에서 관리)
      if (setLoginDialogOpen) {
        setLoginDialogOpen(false);
        console.log('🔒 구글 로그인 모달 닫기 완료');
      }
      
      if (setAuthLoading) {
        setAuthLoading(true);
        console.log('🔄 구글 로그인 로딩 시작');
      }
      
      await Browser.open({ url: googleLoginUrl });
      
      // 🆕 iOS 전용: 로그인 완료 대기 후 브라우저 자동 닫기 (안전한 닫기)
      if (platform === 'ios') {
        console.log('🍎 iOS 브라우저 자동 닫기 타이머 시작...');
        
        // 30초 후 브라우저 안전하게 닫기
        setTimeout(async () => {
          try {
            await Browser.close();
            console.log('🍎 iOS 브라우저 자동 닫기 완료');
          } catch (error) {
            // "No active window to close!" 에러는 정상적인 상황이므로 로그만 남김
            if (error.message?.includes('No active window to close')) {
              console.log('🍎 iOS 브라우저 이미 닫힘 (정상)');
            } else {
              console.log('🍎 iOS 브라우저 닫기 실패:', error.message);
            }
          }
        }, 30000); // 30초
      }
      
      // 이후 처리는 App.jsx의 appUrlOpen 리스너에서 계속됩니다.
      return true; // ✅ 성공 반환 (모달 닫기 위해 필요)
    } else {
      // 웹 플랫폼에서는 현재 창에서 리디렉트
      console.log('🔐 Web Google login request...');
      
      // 로딩 시작 (모달은 LoginDialog에서 관리)
      if (setLoginDialogOpen) {
        setLoginDialogOpen(false);
        console.log('🔒 구글 로그인 모달 닫기 완료');
      }
      
      if (setAuthLoading) {
        setAuthLoading(true);
        console.log('🔄 구글 로그인 로딩 시작');
      }
      
      window.location.href = googleLoginUrl;
      return true; // ✅ 성공 반환
    }
  };

  /**
   * 🎯 메인 구글 OAuth 인증 처리 함수 (조건부 라우팅)
   */
  const handleGoogleAuth = async () => {
    try {
      const platform = Capacitor.getPlatform();
      console.log(`🔐 [${platform}] 구글 로그인 시작...`);
      
      // 🔧 모든 플랫폼에서 기존 백엔드 OAuth 방식 사용 (안정성 우선)
      console.log(`🔙 [${platform}] 기존 백엔드 OAuth 방식 사용 (모든 플랫폼 통일)`);
      const result = await handleOriginalAuth(platform);
      return result; // ✅ 성공 시 true 반환
      
    } catch (error) {
      console.error('🚨 구글 로그인 실패:', error);
      alert(`구글 로그인에 실패했습니다. ${error.message}`);
      return false; // ❌ 실패 시 false 반환
    }
  };

  return {
    handleGoogleAuth,
  };
}; 